import {
  getSEOIntentConfigForSource,
  type SEOIntent,
  type SEOReportPlanId,
} from "@/lib/seo-intents";

export interface ReportInputBirthContext {
  name?: string;
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
}

export interface PageReportOffer {
  intent: SEOIntent;
  planId: SEOReportPlanId;
}

function clean(value: string | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

function appendBirthContext(params: URLSearchParams, birthContext: ReportInputBirthContext): void {
  const values = {
    name: clean(birthContext.name),
    dob: birthContext.birthDate ?? "",
    time: birthContext.birthTime ?? "",
    place: clean(birthContext.birthPlace),
  };
  for (const [key, value] of Object.entries(values)) {
    if (value) params.set(key, value);
  }
}

/**
 * Resolve the report sold by a public page through the same source -> intent ->
 * plan projection used by generate-seo-page-plan-map.mjs. Runtime CTAs must use
 * this boundary instead of hand-picking a plan id in page code (rule 40).
 */
export function resolvePageReportOffer(sourcePath: string): PageReportOffer {
  const config = getSEOIntentConfigForSource(sourcePath);
  return { intent: config.intent, planId: config.reportPlanId };
}

/**
 * Build the acquisition handoff without putting birth details in a server-loggable
 * query string. The hash is read by KundaliFormCard's direct-prefill path.
 */
export function buildReportInputHref({
  sourcePath,
  intent,
  planId,
  language = "en",
  birthContext = {},
}: PageReportOffer & {
  sourcePath: string;
  language?: "en" | "hi";
  birthContext?: ReportInputBirthContext;
}): string {
  const params = new URLSearchParams({
    intent,
    source: sourcePath,
    next_report: planId,
  });
  appendBirthContext(params, birthContext);

  const formPath = language === "hi" ? "/hi/free-kundali/" : "/free-kundali/";
  return `${formPath}#${params.toString()}`;
}
